import { MEMBER_CODE_QUERY_PARAM, MEMBER_CODE_REMEMBER_DAYS } from '@yoyoclub/shared';

/**
 * A member's promo code that arrived on a shared link (docs/prd-member-rewards.md
 * §5.2). Remembered for a while so checkout can fill it in; the latest link wins.
 * Storage can be unavailable (private mode, blocked site data), so every access
 * degrades to "nothing remembered".
 */

const STORAGE_KEY = 'yoyo_member_code';
const CODE_PATTERN = /^[A-Z0-9-]{1,20}$/;

interface Stored {
  code: string;
  savedAt: number;
}

/** Remember the code in `search` (a location.search string), if any. */
export function captureMemberCode(search: string): void {
  const raw = new URLSearchParams(search).get(MEMBER_CODE_QUERY_PARAM);
  const code = raw?.trim().toUpperCase();
  if (!code || !CODE_PATTERN.test(code)) return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ code, savedAt: Date.now() } satisfies Stored)
    );
  } catch {
    // Storage blocked: the visitor can still type the code.
  }
}

export function getRememberedMemberCode(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<Stored>;
    const maxAge = MEMBER_CODE_REMEMBER_DAYS * 24 * 60 * 60 * 1000;
    if (
      typeof stored.code !== 'string' ||
      !CODE_PATTERN.test(stored.code) ||
      typeof stored.savedAt !== 'number' ||
      Date.now() - stored.savedAt > maxAge
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored.code;
  } catch {
    return null;
  }
}

/** Forget the remembered code: it was used, or it does not work for this visitor. */
export function forgetMemberCode(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up.
  }
}

/** A site path with the member's code attached, keeping any query and hash it had. */
export function withMemberCode(path: string, code: string): string {
  const hashAt = path.indexOf('#');
  const hash = hashAt >= 0 ? path.slice(hashAt) : '';
  const base = hashAt >= 0 ? path.slice(0, hashAt) : path;
  const joiner = base.includes('?') ? '&' : '?';
  return `${base}${joiner}${MEMBER_CODE_QUERY_PARAM}=${encodeURIComponent(code)}${hash}`;
}

const MEMBER_PROMO_REASONS = new Set([
  'MEMBER_CODE_OWN',
  'MEMBER_CODE_NOT_NEW',
  'MEMBER_CODE_ALREADY_USED',
  'MEMBER_CODE_NOT_ELIGIBLE',
]);

/** Localized text for a member-code refusal; other promo failures keep the server message. */
export function promoFailureText(
  t: (key: string) => string,
  reason: string | undefined,
  message: string
): string {
  return reason && MEMBER_PROMO_REASONS.has(reason) ? t(`rewards:promoReasons.${reason}`) : message;
}
